import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  Ticket,
  CheckSquare,
  FolderKanban,
  BookOpen,
  Users,
  BarChart3,
  Columns3,
  LayoutGrid,
  Settings,
  Plus,
  type LucideIcon,
} from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/Command';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isInternal: boolean;
}

interface PaletteItem {
  label: string;
  icon: LucideIcon;
  path: string;
  keywords?: string;
  internalOnly?: boolean;
}

const navigationItems: PaletteItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', keywords: 'inicio home' },
  { label: 'Conversaciones', icon: MessageSquare, path: '/conversaciones', keywords: 'chat mensajes' },
  { label: 'Tickets', icon: Ticket, path: '/tickets', keywords: 'soporte incidencias' },
  { label: 'Actividades', icon: CheckSquare, path: '/actividades', keywords: 'tareas', internalOnly: true },
  { label: 'Proyectos', icon: FolderKanban, path: '/proyectos', keywords: 'fases sprints', internalOnly: true },
  { label: 'Kanban', icon: Columns3, path: '/kanban', keywords: 'tablero board', internalOnly: true },
  { label: 'Base de Conocimiento', icon: BookOpen, path: '/conocimiento', keywords: 'articulos ayuda faq' },
  { label: 'Clientes', icon: Users, path: '/clientes', keywords: 'empresas contactos', internalOnly: true },
  { label: 'Reportes', icon: BarChart3, path: '/reportes', keywords: 'estadisticas metricas', internalOnly: true },
  { label: 'Dashboards', icon: LayoutGrid, path: '/dashboards', keywords: 'graficos widgets', internalOnly: true },
  { label: 'Configuración', icon: Settings, path: '/configuracion', keywords: 'ajustes preferencias', internalOnly: true },
];

const quickActions: PaletteItem[] = [
  { label: 'Nueva Conversación', icon: Plus, path: '/conversaciones?action=new', keywords: 'crear chat' },
  { label: 'Nuevo Ticket', icon: Plus, path: '/tickets?action=new', keywords: 'crear incidencia' },
  { label: 'Nueva Actividad', icon: Plus, path: '/actividades?action=new', keywords: 'crear tarea', internalOnly: true },
];

export function CommandPalette({ open, onOpenChange, isInternal }: CommandPaletteProps) {
  const navigate = useNavigate();

  const filteredNav = useMemo(
    () => navigationItems.filter((item) => !item.internalOnly || isInternal),
    [isInternal]
  );

  const filteredActions = useMemo(
    () => quickActions.filter((item) => !item.internalOnly || isInternal),
    [isInternal]
  );

  const runCommand = useCallback(
    (path: string) => {
      onOpenChange(false);
      navigate(path);
    },
    [navigate, onOpenChange]
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Buscar páginas, acciones..." />
      <CommandList>
        <CommandEmpty>No se encontraron resultados.</CommandEmpty>

        <CommandGroup heading="Navegación">
          {filteredNav.map((item) => (
            <CommandItem
              key={item.path}
              value={`${item.label} ${item.keywords || ''}`}
              onSelect={() => runCommand(item.path)}
            >
              <item.icon className="mr-2 h-4 w-4" />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Acciones rápidas">
          {filteredActions.map((item) => (
            <CommandItem
              key={item.path}
              value={`${item.label} ${item.keywords || ''}`}
              onSelect={() => runCommand(item.path)}
            >
              <item.icon className="mr-2 h-4 w-4" />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
